import React, { useId, useMemo } from 'react';

type Weight = number | 'normal' | 'bold';

interface StrokeGradientTextProps {
  text: string;
  className?: string;
  textClassName?: string;
  fontSize?: number;
  fontWeight?: Weight;
  strokeWidth?: number;
  widthFactor?: number;
}

const SingerNameText: React.FC<StrokeGradientTextProps> = ({
  text,
  className = '',
  textClassName = 'font-asap_condensed',
  fontSize = 42,
  fontWeight = 700,
  strokeWidth = 2,
  widthFactor = 0.65,
}) => {
  const uuid = useId();

  const { vbW, vbH } = useMemo(() => {
    const H = Math.ceil(fontSize + strokeWidth * 4);
    const W = Math.max(1, Math.ceil(text.length * fontSize * widthFactor) + strokeWidth * 4);
    return { vbW: W, vbH: H };
  }, [text.length, fontSize, strokeWidth, widthFactor]);

  const w1 = 0 * strokeWidth;
  const w2 = 2 * strokeWidth;
  const w3 = 4 * strokeWidth;
  const w4 = 5 * strokeWidth;

  return (
    <div className={`inline-block ${className}`}>
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        width={vbW}                // keep font size constant
        height={vbH}               // keep font size constant
        className="overflow-visible"
        aria-label={text}
        role="img"
      >
        <defs>
          <filter id={`${uuid}-shadow1`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#570056" floodOpacity="1" />
          </filter>

          <filter id={`${uuid}-shadow2`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#570056" floodOpacity="0.75" />
          </filter>

          <linearGradient id={`${uuid}-fill`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="6%"  stopColor="#FFFFFF" />
            <stop offset="18%" stopColor="#FDF8FB" />
            <stop offset="39%" stopColor="#FBF0F7" />
            <stop offset="50%" stopColor="#FFD7F1" />
            <stop offset="59%" stopColor="#FFFFFF" />
            <stop offset="73%" stopColor="#FFFFFF" />
            <stop offset="90%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#F5DEED" />
          </linearGradient>

          <linearGradient id={`${uuid}-stroke2`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#EB12B5" />
            <stop offset="17%"  stopColor="#FFB8E7" />
            <stop offset="27%"  stopColor="#DE009E" />
            <stop offset="41%"  stopColor="#C51E95" />
            <stop offset="54%"  stopColor="#D034A2" />
            <stop offset="67%"  stopColor="#CC0F94" />
            <stop offset="77%"  stopColor="#FFA6E9" />
            <stop offset="87%"  stopColor="#D700A4" />
            <stop offset="100%" stopColor="#78005C" />
          </linearGradient>

          <linearGradient id={`${uuid}-stroke3`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%"   stopColor="#FF55CC" />
            <stop offset="16%"  stopColor="#6D005F" />
            <stop offset="26%"  stopColor="#FF3AB8" />
            <stop offset="36%"  stopColor="#910046" />
            <stop offset="52%"  stopColor="#FFEBF0" />
            <stop offset="76%"  stopColor="#A0000C" />
            <stop offset="89%"  stopColor="#870845" />
            <stop offset="100%" stopColor="#FFD0F5" />
          </linearGradient>

          <linearGradient id={`${uuid}-stroke4`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#FFFFFF" />
            <stop offset="24%"  stopColor="#FFC7EF" />
            <stop offset="48%"  stopColor="#FFEBF0" />
            <stop offset="77%"  stopColor="#FF7CC8" />
            <stop offset="98%"  stopColor="#FFFFFF" />
          </linearGradient>
        </defs>

        <g textAnchor="middle" dominantBaseline="middle" fontWeight={fontWeight} fontSize={fontSize}>
          {/* Layer 4 */}
          <text
            x="50%" y="50%"
            className={textClassName}
            fill="none"
            stroke={`url(#${uuid}-stroke4)`}
            strokeWidth={w4}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            letterSpacing={2}
          >
            {text}
          </text>

          {/* Layer 3 */}
          <text
            x="50%" y="50%"
            className={textClassName}
            fill="none"
            stroke={`url(#${uuid}-stroke3)`}
            strokeWidth={w3}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={0.75}
            letterSpacing={2}
          >
            {text}
          </text>

          {/* Layer 2 */}
          <text
            x="50%" y="50%"
            className={textClassName}
            fill="none"
            stroke={`url(#${uuid}-stroke2)`}
            strokeWidth={w2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            filter={`url(#${uuid}-shadow2)`}
            letterSpacing={2}
          >
            {text}
          </text>

          {/* Layer 1 */}
          <text
            x="50%" y="50%"
            className={textClassName}
            fill={`url(#${uuid}-fill)`}
            stroke="#D052F3"
            strokeWidth={w1}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            filter={`url(#${uuid}-shadow1)`}
            letterSpacing={2}
          >
            {text}
          </text>
        </g>
      </svg>
    </div>
  );
};

export default React.memo(SingerNameText);

